import React from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { DashboardUserProject } from '../../../store/reducer/DashboardReducer';
import Badge from '../../shared/TailwindComponents/Badge';
import Button from '../../shared/TailwindComponents/Button';
import ProfileImage from '../../Header/ProfileImage';
import { BadgeColor } from '../../shared/TailwindComponents/types/badgeTypes';
import { formatDateTime } from '../../../utils/functions';
import {
  PiProjectorScreenChartLight,
  PiArrowRightLight,
  PiCalendarLight,
  PiLinkSimpleLight,
  PiUsersThreeLight,
  PiPlusLight,
} from 'react-icons/pi';

interface MyProjectsProps {
  projects: DashboardUserProject[];
  loading?: boolean;
}

const getPriorityDot = (priority: string): string => {
  switch (priority?.toLowerCase()) {
    case 'urgent':   return 'bg-red-500';
    case 'high':     return 'bg-orange-400';
    case 'medium':   return 'bg-amber-400';
    case 'low':
    default:         return 'bg-gray-300 dark:bg-gray-600';
  }
};

const getPriorityColor = (priority: string): BadgeColor => {
  switch (priority?.toLowerCase()) {
    case 'urgent': return 'red';
    case 'high':   return 'orange';
    case 'medium': return 'yellow';
    default:       return 'gray';
  }
};

const getDueDateInfo = (dueDate: string | null): { text: string; className: string } => {
  if (!dueDate) return { text: '', className: '' };
  const due = new Date(dueDate);
  const now = new Date();
  const diffDays = Math.ceil((due.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

  if (diffDays < 0) return {
    text: `${Math.abs(diffDays)}d overdue`,
    className: 'text-red-600 dark:text-red-400',
  };
  if (diffDays === 0) return { text: 'Due today', className: 'text-amber-600 dark:text-amber-400' };
  if (diffDays <= 7) return { text: `${diffDays}d left`, className: 'text-amber-500 dark:text-amber-400' };
  return { text: `${diffDays}d left`, className: 'text-gray-500 dark:text-gray-400' };
};

const AvatarStack: React.FC<{ collaborators: DashboardUserProject['collaborators']; total: number }> = ({
  collaborators,
  total,
}) => {
  const shown = collaborators.slice(0, 4);
  const overflow = total - shown.length;

  return (
    <div className="flex items-center -space-x-1.5">
      {shown.map((c, i) => (
        <div
          key={i}
          className="border-2 border-white dark:border-gray-800 rounded-full flex-shrink-0"
          title={c.name}
        >
          <ProfileImage
            profilePicture={c.profile_picture || null}
            email={''}
            initials={c.initials || '??'}
            name={c.name || ''}
            size="w-6 h-6"
          />
        </div>
      ))}
      {overflow > 0 && (
        <div className="w-6 h-6 rounded-full border-2 border-white dark:border-gray-800
          bg-gray-100 dark:bg-gray-700 flex items-center justify-center
          text-[9px] text-gray-500 dark:text-gray-400">
          +{overflow}
        </div>
      )}
    </div>
  );
};

const ProjectCard: React.FC<{ project: DashboardUserProject; onClick: () => void }> = ({
  project,
  onClick,
}) => {
  const dueInfo = getDueDateInfo(project.due_date);

  return (
    <div
      onClick={onClick}
      className="group relative flex-shrink-0 w-72 bg-white dark:bg-gray-800 rounded-xl border border-gray-200/80 dark:border-gray-700/80
        p-4 cursor-pointer hover:shadow-md hover:border-gray-300 dark:hover:border-gray-600
        transition-all duration-200 flex flex-col gap-3 snap-start"
    >
      {/* Header: title + arrow */}
      <div className="flex items-start gap-2.5">
        <h3 className="flex-1 text-sm text-gray-900 dark:text-gray-100 leading-snug line-clamp-2">
          {project.title}
        </h3>
        <PiArrowRightLight className="flex-shrink-0 w-3.5 h-3.5 text-gray-300 dark:text-gray-600 opacity-0 group-hover:opacity-100 transition-opacity mt-0.5" />
      </div>

      {/* Badges row */}
      <div className="flex items-center gap-1.5 flex-wrap">
        <Badge color="teal">
          {project.status.charAt(0).toUpperCase() + project.status.slice(1)}
        </Badge>
        <Badge color={getPriorityColor(project.priority)}>
          {project.priority.charAt(0).toUpperCase() + project.priority.slice(1)}
        </Badge>
      </div>

      {/* Due date */}
      {project.due_date && (
        <div className={`flex items-center gap-1 text-xs ${dueInfo.className}`}>
          <PiCalendarLight className="w-3.5 h-3.5 flex-shrink-0" />
          <span>{dueInfo.text}</span>
          <span className="text-gray-400 dark:text-gray-600 ml-0.5">
            ({formatDateTime(project.due_date)})
          </span>
        </div>
      )}

      {/* Footer: avatars + resource count */}
      <div className="flex items-center justify-between mt-auto pt-1 border-t border-gray-100 dark:border-gray-700/60">
        <AvatarStack
          collaborators={project.collaborators}
          total={project.collaborator_count}
        />

        <div className="flex items-center gap-2.5 text-xs text-gray-400 dark:text-gray-500">
          <span className="flex items-center gap-1">
            <PiLinkSimpleLight className="w-3.5 h-3.5" />
            {project.resource_count}
          </span>
          <span className="flex items-center gap-1">
            <PiUsersThreeLight className="w-3.5 h-3.5" />
            {project.collaborator_count}
          </span>
        </div>
      </div>
    </div>
  );
};

const NewProjectCard: React.FC<{ onClick: () => void }> = ({ onClick }) => {
  const { t } = useTranslation();
  return (
    <div
      onClick={onClick}
      className="group flex-shrink-0 w-72 rounded-xl border-2 border-dashed border-gray-200 dark:border-gray-700
        cursor-pointer snap-start min-h-[160px]
        hover:border-teal-300 dark:hover:border-teal-600
        hover:bg-teal-50/40 dark:hover:bg-teal-900/10
        transition-all duration-200
        flex flex-col items-center justify-center gap-2"
    >
      <div className="inline-flex items-center justify-center w-10 h-10 rounded-full
        bg-gray-100 dark:bg-gray-700
        group-hover:bg-teal-100 dark:group-hover:bg-teal-900/30
        transition-colors">
        <PiPlusLight className="w-5 h-5 text-gray-400 dark:text-gray-500 group-hover:text-teal-600 dark:group-hover:text-teal-400 transition-colors" />
      </div>
      <span className="text-xs text-gray-400 dark:text-gray-500 group-hover:text-teal-600 dark:group-hover:text-teal-400 transition-colors">
        {t('New Project')}
      </span>
    </div>
  );
};

const SkeletonCard: React.FC = () => (
  <div className="flex-shrink-0 w-72 bg-white dark:bg-gray-800 rounded-xl border border-gray-200/80 dark:border-gray-700/80 p-4 flex flex-col gap-3 snap-start">
    <div className="flex items-start gap-2.5">
      <div className="mt-1.5 w-2 h-2 rounded-full bg-gray-200 dark:bg-gray-700 flex-shrink-0" />
      <div className="flex-1 space-y-1.5">
        <div className="h-3.5 bg-gray-200 dark:bg-gray-700 rounded animate-fadePulse w-4/5" />
        <div className="h-3.5 bg-gray-200 dark:bg-gray-700 rounded animate-fadePulse w-3/5" />
      </div>
    </div>
    <div className="flex gap-1.5">
      <div className="h-5 w-14 bg-gray-200 dark:bg-gray-700 rounded-full animate-fadePulse" />
      <div className="h-5 w-16 bg-gray-200 dark:bg-gray-700 rounded-full animate-fadePulse" />
    </div>
    <div className="h-3 w-24 bg-gray-200 dark:bg-gray-700 rounded animate-fadePulse" />
    <div className="flex items-center justify-between pt-1 border-t border-gray-100 dark:border-gray-700/60">
      <div className="flex -space-x-1.5">
        {[0, 1, 2].map(i => (
          <div key={i} className="w-6 h-6 rounded-full bg-gray-200 dark:bg-gray-700 border-2 border-white dark:border-gray-800 animate-fadePulse" />
        ))}
      </div>
      <div className="h-3 w-12 bg-gray-200 dark:bg-gray-700 rounded animate-fadePulse" />
    </div>
  </div>
);

const MyProjects: React.FC<MyProjectsProps> = ({ projects, loading }) => {
  const { t } = useTranslation();
  const history = useHistory();

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200/80 dark:border-gray-700/80 overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-3.5 bg-white dark:bg-gray-800 border-b border-gray-100 dark:border-gray-700">
        <div className="flex items-center gap-2.5">
          <PiProjectorScreenChartLight className="w-4 h-4 text-gray-600 dark:text-gray-400" />
          <h3 className="text-sm text-gray-900 dark:text-gray-100">
            {t('My Projects')}
          </h3>
          {projects.length > 0 && (
            <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-xs rounded-full bg-teal-100 text-teal-700 dark:bg-teal-900/40 dark:text-teal-300">
              {projects.length}
            </span>
          )}
        </div>

        <Button variant="outline" size="small" onClick={() => history.push('/projects')}>
          {t('View All')}
        </Button>
      </div>

      {/* Scrollable card row */}
      <div className="px-5 pt-4 bg-gray-50 dark:bg-gray-900/30">
        {loading ? (
          <div className="flex gap-3 overflow-x-auto custom-scrollbar-main pb-3 snap-x snap-mandatory scrollbar-thin scrollbar-thumb-gray-200 dark:scrollbar-thumb-gray-700">
            {[0, 1, 2, 3].map(i => <SkeletonCard key={i} />)}
          </div>
        ) : projects.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-10 text-center">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-teal-50 dark:bg-teal-900/20 mb-3">
              <PiProjectorScreenChartLight className="w-6 h-6 text-teal-400 dark:text-teal-500" />
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
              {t('No projects yet - create one to organize your work')}
            </p>
            <Button
              variant="primary"
              size="small"
              onClick={() => history.push('/projects?add=true')}
            >
              <PiPlusLight className="w-3.5 h-3.5 mr-1" />
              {t('Create Project')}
            </Button>
          </div>
        ) : (
          <div className="flex gap-3 overflow-x-auto custom-scrollbar-main pb-3 snap-x snap-mandatory scrollbar-thin scrollbar-thumb-gray-200 dark:scrollbar-thumb-gray-700">
            {projects.map((project) => (
              <ProjectCard
                key={project.uuid}
                project={project}
                onClick={() => history.push(`/projects/${project.uuid}`)}
              />
            ))}
            <NewProjectCard onClick={() => history.push('/projects?add=true')} />
          </div>
        )}
      </div>
    </div>
  );
};

export default MyProjects;
